import React, { useState } from 'react';
import { Button, Modal, Table, Select, InputNumber, Space, Tooltip, message, Alert, Checkbox } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

export default function EntradaEstoqueNFe({ nota, onOk }) {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [depositoId, setDepositoId] = useState(null);
  const [linhas, setLinhas] = useState([]);
  const [jaLancada, setJaLancada] = useState(false);
  const [atualizarCusto, setAtualizarCusto] = useState(true);

  const abrir = async () => {
    setAberto(true);
    setCarregando(true);
    try {
      const [rn, rp, rd] = await Promise.all([
        api.get('/estoque/entrada-nfe/' + nota.id),
        api.get('/produtos'),
        api.get('/estoque/depositos'),
      ]);
      setProdutos(rp.data || []);
      const deps = rd.data || [];
      setDepositos(deps);
      if (deps.length === 1) setDepositoId(deps[0].id);
      setJaLancada(!!rn.data.ja_lancada);
      const itens = Array.isArray(rn.data.itens) ? rn.data.itens : [];
      setLinhas(itens.map((it, i) => ({
        key: i,
        xProd: it.xProd || '',
        cProd: it.cProd || '',
        NCM: it.NCM || '',
        uCom: it.uCom || '',
        quantidade: parseFloat(it.qCom) || 0,
        custo: parseFloat(it.vUnCom) || 0,
        produto_id: null,
      })));
    } catch (e) {
      message.error((e.response && e.response.data && e.response.data.erro) || 'Erro ao carregar a nota');
    } finally {
      setCarregando(false);
    }
  };

  const mudar = (key, campo, valor) => setLinhas(l => l.map(x => (x.key === key ? Object.assign({}, x, { [campo]: valor }) : x)));

  const confirmar = async () => {
    const vinculados = linhas.filter(l => l.produto_id);
    if (!depositoId) { message.warning('Selecione o dep\u00f3sito'); return; }
    if (!vinculados.length) { message.warning('Vincule ao menos um item a um produto'); return; }
    setSalvando(true);
    try {
      const r = await api.post('/estoque/entrada-nfe/' + nota.id, {
        deposito_id: depositoId,
        atualizar_custo: atualizarCusto,
        itens: vinculados.map(l => ({ produto_id: l.produto_id, quantidade: l.quantidade, custo: l.custo })),
      });
      message.success('Entrada lan\u00e7ada: ' + r.data.itens + ' item(ns)');
      setAberto(false);
      if (onOk) onOk();
    } catch (e) {
      message.error((e.response && e.response.data && e.response.data.erro) || 'Erro ao lan\u00e7ar a entrada');
    } finally {
      setSalvando(false);
    }
  };

  const colunas = [
    {
      title: 'Item do fornecedor',
      dataIndex: 'xProd',
      render: (v, r) => (
        <div>
          <div>{v}</div>
          <div style={{ fontSize: 11, color: '#888' }}>
            {'c\u00f3d. ' + (r.cProd || '-') + ' \u00b7 NCM ' + (r.NCM || '-') + ' \u00b7 ' + (r.uCom || '')}
          </div>
        </div>
      ),
    },
    {
      title: 'Produto do cadastro',
      width: 320,
      render: (_, r) => (
        <Select showSearch allowClear optionFilterProp="children" style={{ width: '100%' }}
          placeholder="Selecione o produto" value={r.produto_id}
          onChange={v => mudar(r.key, 'produto_id', v || null)}>
          {produtos.map(p => <Option key={p.id} value={p.id}>{(p.codigo ? p.codigo + ' - ' : '') + p.descricao}</Option>)}
        </Select>
      ),
    },
    {
      title: 'Qtd',
      width: 110,
      render: (_, r) => <InputNumber min={0} step={1} style={{ width: '100%' }} value={r.quantidade} onChange={v => mudar(r.key, 'quantidade', v)} />,
    },
    {
      title: 'Custo unit.',
      width: 130,
      render: (_, r) => <InputNumber min={0} step={0.01} style={{ width: '100%' }} value={r.custo} onChange={v => mudar(r.key, 'custo', v)} />,
    },
  ];

  const completa = nota.tipo === 'completa';

  return (
    <>
      <Tooltip title={completa ? 'Dar entrada no estoque' : 'Dispon\u00edvel s\u00f3 com o XML completo'}>
        <Button size="small" icon={<ImportOutlined />} disabled={!completa} onClick={abrir} />
      </Tooltip>
      <Modal open={aberto} onCancel={() => setAberto(false)} width={1000} style={{ top: 20 }}
        title={'Entrada no estoque \u2014 ' + (nota.razao_social_emit || '')}
        okText={'Lan\u00e7ar entrada'} cancelText="Cancelar"
        confirmLoading={salvando} onOk={confirmar}>
        {jaLancada && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message={'Esta nota j\u00e1 teve entrada lan\u00e7ada'}
            description={'Confirmar de novo vai somar as quantidades outra vez no estoque.'} />
        )}
        <Space style={{ marginBottom: 12 }} wrap>
          <span>{'Dep\u00f3sito:'}</span>
          <Select style={{ width: 260 }} placeholder={'Selecione o dep\u00f3sito'} value={depositoId} onChange={setDepositoId}>
            {depositos.map(d => <Option key={d.id} value={d.id}>{d.nome}</Option>)}
          </Select>
          <Checkbox checked={atualizarCusto} onChange={e => setAtualizarCusto(e.target.checked)}>
            {'Atualizar o custo do produto com o valor da nota'}
          </Checkbox>
        </Space>
        <Table size="small" loading={carregando} pagination={false} rowKey="key"
          dataSource={linhas} columns={colunas}
          locale={{ emptyText: 'A nota n\u00e3o tem itens' }} />
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          {'Itens sem produto selecionado s\u00e3o ignorados \u2014 d\u00e1 para lan\u00e7ar em partes.'}
        </div>
      </Modal>
    </>
  );
}
